import { Plugin, PluginKey } from 'prosemirror-state';

// WhatsApp-style bold: typing a single *word* turns it BOLD as you type.
//
// WhatsApp uses * for bold and _ for italic, but markdown (and the editor's default
// rules) treat a single * as italic. This plugin is PREPENDED for WhatsApp inboxes
// so its handleTextInput wins over the package's *->em input rule. The strong mark
// serializes to markdown **word**, which the Evolution bridge converts to WhatsApp
// *word* (= bold) on the wire. `_italic_` and `**bold**` stay with the default rules.
//
// Deliberately NOT built with prosemirror-inputrules' inputRules(): the Vite bundle
// carries two prosemirror-state instances (app's vs the vendored
// @chatwoot/prosemirror-schema's), and unkeyed plugins take auto-keys from a
// per-instance counter — so an unkeyed inputRules() plugin collides with the
// package's first plugin ("Adding different instances of a keyed plugin (plugin$)")
// and EditorState.create throws. An explicit unique PluginKey makes the collision
// impossible no matter how modules resolve.
//
// Same regex shape as the package's italicRegex2 (marks.js) — no lookbehind. The
// prefix group captures any non-space run before the opening *; a non-empty prefix
// (e.g. "a" in a*b*, or "*" in **b*) means mid-word or inside a ** pair, so we bail
// — matching both the package's behavior and WhatsApp's own word-boundary rule.
const WA_BOLD_REGEX = /(\S*)(\*([^\s*][^*]*[^\s*]|[^\s*])\*)$/;
const MAX_MATCH = 500;

// Mirrors prosemirror-inputrules' run(): the just-typed closing * is NOT yet in the
// doc, so match against textBefore+typed; the match starts at the opening *. Mark
// the content, delete the opening *, and consume the closing * by returning true.
const handleTextInput = (view, from, to, text) => {
  try {
    if (text !== '*' || view.composing) return false;
    const { state } = view;
    const strong = state.schema.marks.strong;
    if (!strong) return false;
    const $from = state.doc.resolve(from);
    if ($from.parent.type.spec.code) return false;
    const textBefore =
      $from.parent.textBetween(
        Math.max(0, $from.parentOffset - MAX_MATCH),
        $from.parentOffset,
        null,
        '￼'
      ) + text;
    const match = WA_BOLD_REGEX.exec(textBefore);
    if (!match) return false;
    const [, prefix, , inner] = match;
    if (prefix || !inner) return false;
    const start = from - (match[0].length - text.length);
    // Don't autoformat inside inline code (mirrors the package's addMark guard).
    if (
      state.schema.marks.code &&
      state.schema.marks.code.isInSet(state.doc.resolve(start + 1).marks())
    ) {
      return false;
    }
    view.dispatch(
      state.tr
        .addMark(start + 1, to, strong.create())
        .delete(start, start + 1)
        .removeStoredMark(strong)
    );
    return true;
  } catch (e) {
    // Fail open: fall through to the default rules (italic) rather than break typing.
    // eslint-disable-next-line no-console
    console.error('[whatsapp-bold] handler failed, falling through:', e);
    return false;
  }
};

/**
 * Editor plugin that makes single *word* bold. Prepend it to the editor plugin list
 * (buildEditor prepends app plugins, so its handleTextInput runs first).
 * @param {import('prosemirror-model').Schema} schema - must have a `strong` mark.
 * @returns {import('prosemirror-state').Plugin|null}
 */
export const whatsappBoldInputRules = schema => {
  if (!schema.marks.strong) return null;
  return new Plugin({
    key: new PluginKey('whatsappBoldInput'),
    props: { handleTextInput },
  });
};
